import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';
import { POLL_INTERVALS } from '../config/appPolling';

const EMPTY_SUMMARY = { unreadTotal: 0, recent: [], popups: [] };

export function useInbox(enabled = true) {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [visiblePopups, setVisiblePopups] = useState([]);
  const shownIdsRef = useRef(new Set());

  const fetchSummary = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await api.get('/inbox/summary');
      if (!res.data.success) return;
      const data = res.data.data || EMPTY_SUMMARY;
      setSummary(data);

      const pending = data.popups || [];
      const fresh = pending.filter((n) => !shownIdsRef.current.has(n.id));
      if (fresh.length) {
        fresh.forEach((n) => shownIdsRef.current.add(n.id));
        setVisiblePopups((prev) => {
          const merged = [...prev];
          fresh.forEach((item) => {
            if (!merged.some((m) => m.id === item.id)) merged.push(item);
          });
          return merged;
        });
      }
    } catch {
      /* 静默失败 */
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setSummary(EMPTY_SUMMARY);
      setVisiblePopups([]);
      shownIdsRef.current = new Set();
      return undefined;
    }
    fetchSummary();
    const timer = setInterval(fetchSummary, POLL_INTERVALS.inbox);
    return () => clearInterval(timer);
  }, [enabled, fetchSummary]);

  const dismissPopup = useCallback(async (messageId) => {
    setVisiblePopups((prev) => prev.filter((p) => p.id !== messageId));
    try {
      await api.post('/inbox/popups/ack', { ids: [messageId] });
    } catch {
      /* ignore */
    }
  }, []);

  const dismissAllPopups = useCallback(async () => {
    const ids = visiblePopups.map((p) => p.id);
    setVisiblePopups([]);
    if (ids.length) {
      try {
        await api.post('/inbox/popups/ack', { ids });
      } catch {
        /* ignore */
      }
    }
  }, [visiblePopups]);

  const markRead = useCallback(async (ids) => {
    if (!ids?.length) return;
    try {
      const res = await api.post('/inbox/read', { ids });
      if (res.data.success) {
        setVisiblePopups((prev) => prev.filter((p) => !ids.includes(p.id)));
        setSummary((prev) => ({
          ...prev,
          unreadTotal: res.data.data?.unreadTotal ?? prev.unreadTotal
        }));
        await fetchSummary();
      }
    } catch {
      /* ignore */
    }
  }, [fetchSummary]);

  const markAllRead = useCallback(async (category) => {
    try {
      const res = await api.post('/inbox/read', { all: true, category: category || undefined });
      if (res.data.success) {
        setVisiblePopups([]);
        await fetchSummary();
      }
    } catch {
      /* ignore */
    }
  }, [fetchSummary]);

  const refresh = useCallback(() => fetchSummary(), [fetchSummary]);

  return {
    unreadTotal: summary.unreadTotal || 0,
    recent: summary.recent || [],
    visiblePopups,
    dismissPopup,
    dismissAllPopups,
    markRead,
    markAllRead,
    refresh
  };
};
